// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useTaskQuery } from "@/api/hooks/use-tasks";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TaskAvailabilityTab } from "@/features/tasks/task-availability-tab";
import { TaskInfoTab } from "@/features/tasks/task-info-tab";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

export const Route = createFileRoute("/_authenticated/tasks/$subtype/")({
  // The Edit tab moved to `/tasks/$subtype/edit`. Old links and bookmarks carrying `?t=edit` are
  // forwarded rather than silently dropped to Info.
  beforeLoad: ({ params, search }) => {
    if (search.t === "edit") throw redirect({ to: "/tasks/$subtype/edit", params, search: {} });
  },
  component: TaskDetailPage,
});

function TaskDetailPage() {
  const { subtype } = Route.useParams();
  const { t: tab } = Route.useSearch();
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canSeeAvailability = hasPermission(PAGE_PERMISSIONS.tasksAvailability);
  // Already loaded by the layout, which only renders this page once the task is in hand.
  const { data: task } = useTaskQuery(subtype);

  // A built-in type's availability exists too — one `action_task_settings` row per store — so the
  // tab shows for both kinds.
  const tabAllowed: Record<string, boolean> = {
    info: true,
    availability: canSeeAvailability,
  };
  const requestedTab = tab ?? "info";
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";

  function handleTabChange(newTab: string) {
    navigate({
      to: "/tasks/$subtype",
      params: { subtype },
      search: newTab === "info" ? {} : { t: newTab },
      replace: true,
    });
  }

  if (!task) return null;

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
      <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
        <TabsList>
          <TabsIndicator />
          <TabsTrigger value="info">{t("tasks.detail.tabs.info")}</TabsTrigger>
          {canSeeAvailability && <TabsTrigger value="availability">{t("tasks.detail.tabs.availability")}</TabsTrigger>}
        </TabsList>
      </div>
      <TabsContent value="info" className="space-y-4">
        <TaskInfoTab task={task} />
      </TabsContent>
      {canSeeAvailability && (
        <TabsContent value="availability" className="space-y-4">
          <TaskAvailabilityTab subtype={subtype} kind={task.kind} active={activeTab === "availability"} />
        </TabsContent>
      )}
    </Tabs>
  );
}
